import { useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DesktopSideMenu from './drawer/DesktopSideMenu';
import MobileSideMenu from './drawer/MobileSideMenu';
import { SideMenuList } from './menu/SideMenuList';
import { MENU_ITEMS } from './config/menuConfig';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../features/permissions/usePermissions';
import type { NavigationSideMenuProps } from './NavigationSideMenu.types';
import { filterMenuByPermissions, resolveMenuItemsWithState } from './NavigationSideMenu.utils';

const HOVER_ENTER_DELAY = 120;
const HOVER_LEAVE_DELAY = 200;

export function NavigationSideMenu({
  drawerOpen,
  onCloseMobile,
  expandedKeys,
  onToggleExpand,
}: NavigationSideMenuProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { can } = usePermissions();
  const [hovering, setHovering] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleMouseEnter = useCallback(() => {
    if (!canHover) return;
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setHovering(true), HOVER_ENTER_DELAY);
  }, [canHover]);

  const handleMouseLeave = useCallback(() => {
    if (!canHover) return;
    clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setHovering(false), HOVER_LEAVE_DELAY);
  }, [canHover]);

  useEffect(() => {
    return () => clearTimeout(hoverTimeout.current);
  }, []);

  const currentPath = location.pathname;
  const effectiveOpen = drawerOpen || hovering;

  const itemsWithState = useMemo(
    () =>
      resolveMenuItemsWithState(
        filterMenuByPermissions(MENU_ITEMS, can),
        currentPath,
        expandedKeys,
      ),
    [currentPath, expandedKeys, can],
  );

  const sideMenuContent = (
    <SideMenuList
      items={itemsWithState}
      onNavigate={path => {
        if (!path) return;
        navigate(path);
        if (isMobile) onCloseMobile();
      }}
      onToggleExpand={onToggleExpand}
      showLabel={effectiveOpen || isMobile}
      variant={isMobile ? 'mobile' : 'desktop'}
      translate={t}
    />
  );

  return isMobile ? (
    <MobileSideMenu open={drawerOpen} onClose={onCloseMobile}>
      {sideMenuContent}
    </MobileSideMenu>
  ) : (
    <DesktopSideMenu
      open={drawerOpen}
      hovering={hovering}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {sideMenuContent}
    </DesktopSideMenu>
  );
}
